import { Button } from "primereact/button";
import { ToggleButton } from "primereact/togglebutton";
import { InputNumber } from "primereact/inputnumber";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { IComponentTimed } from "components/pages/dashboard/components/interfaces";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { InputText } from "primereact/inputtext";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { ColumnOptions } from "./EditColumn";
import EditDatasetCellDialog, { CellType, ICellConfig } from "./EditDatasetCellDialog";
import EditBasicCellDialog from "./EditBasicCellDialog";

interface CellValuesEditorProps {
  currentConfig: IUniversalDatasetTableConfig;
  setCurrentConfig: (config: IUniversalDatasetTableConfig) => void;
}

interface RowData {
  columns: IUniversalDatasetTableColumn[];
  rowIndex: number;
  isSeparator?: boolean;
}

export interface IUniversalDatasetTableConfig extends IComponentTimed {
  rows: IUniversalDatasetTableRow[];
  columnsOptions: ColumnOptions[];
  showColumnHeaders?: boolean;
  compactMode?: boolean;
  hideHeader?: boolean;
  updateFrequency?: number | null;
}

export interface IUniversalDatasetTableRow {
  columns: IUniversalDatasetTableColumn[];
  isSeparator?: boolean;
}

export interface IUniversalDatasetTableColumn {
  cell: ICellConfig;
}

const CellValuesEditor = ({ currentConfig, setCurrentConfig }: CellValuesEditorProps) => {
  const [isDatasetValuesVisible, setIsDatasetValuesVisible] = useState<boolean>(false);
  const [isEditDatasetCellDialogVisible, setIsEditDatasetCellDialogVisible] =
    useState<boolean>(false);
  const [isEditBasicCellDialogVisible, setIsEditBasicCellDialogVisible] = useState<boolean>(false);
  const [selectedCell, setSelectedCell] = useState<ICellConfig>();
  const [selectedCellLocation, setSelectedCellLocation] = useState<{
    rowIndex: number;
    columnIndex: number;
  }>();

  const updateCellValue = (rowIndex: number, colIndex: number, value: string | number) => {
    const newConfig = {
      ...currentConfig,
      rows: currentConfig.rows.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            columns: row.columns.map((column, columnIndex) => {
              if (columnIndex === colIndex) {
                return {
                  ...column,
                  cell: {
                    ...column.cell,
                    value,
                  },
                };
              }
              return column;
            }),
          };
        }
        return row;
      }),
    };

    setCurrentConfig(newConfig);
  };

  const handleOpenDatasetCellDialog = (rowData: RowData, columnIndex: number) => {
    setSelectedCellLocation({ rowIndex: rowData.rowIndex, columnIndex });
    setIsEditDatasetCellDialogVisible(true);
    setSelectedCell(rowData.columns[columnIndex].cell);
  };

  const handleOpenBasicCellDialog = (rowData: RowData, columnIndex: number) => {
    setSelectedCellLocation({ rowIndex: rowData.rowIndex, columnIndex });
    setIsEditBasicCellDialogVisible(true);
  };

  const renderDatasetValueEditor = (rowData: RowData, columnIndex: number, cellValue: string) => {
    return (
      <div className="flex flex-row gap-3 justify-content-between">
        {isDatasetValuesVisible ? (
          <DatasetTitle identifier={cellValue} hideData hideChartButton wordBreak />
        ) : (
          <DatasetTitle identifier={cellValue} hideData hideChartButton numberOfLettersToDisplay={8} />
        )}
        <OpenCellConfigDialogButton
          handleOpenBasicCellDialog={() => handleOpenDatasetCellDialog(rowData, columnIndex)}
        />
      </div>
    );
  };

  const renderCell = (rowData: RowData, columnIndex: number) => {
    const cell = rowData.columns[columnIndex]?.cell;
    if (!cell) return null;

    switch (cell.type) {
      case CellType.NUMBER:
        return (
          <div className="flex flex-row justify-content-between">
            <InputNumber
              className="w-full"
              value={(cell.value as number) ?? null}
              onValueChange={e => updateCellValue(rowData.rowIndex, columnIndex, e.value as number)}
            />
            <OpenCellConfigDialogButton
              handleOpenBasicCellDialog={() => handleOpenBasicCellDialog(rowData, columnIndex)}
            />
          </div>
        );

      case CellType.STRING:
        return (
          <div className="flex flex-row justify-content-between">
            <InputText
              className="w-full min-w-10rem"
              value={(cell.value as string) ?? ""}
              onChange={e => updateCellValue(rowData.rowIndex, columnIndex, e.target.value)}
            />
            <OpenCellConfigDialogButton
              handleOpenBasicCellDialog={() => handleOpenBasicCellDialog(rowData, columnIndex)}
            />
          </div>
        );

      case CellType.DATASET:
        return renderDatasetValueEditor(rowData, columnIndex, cell.value as string);

      case CellType.UNIT:
        return (
          <div className="flex flex-row justify-content-between">
            <DatasetUnitSelector
              className="w-full min-w-10rem"
              value={(cell.value as string) ?? ""}
              onChange={e => updateCellValue(rowData.rowIndex, columnIndex, e.value)}
            />
            <OpenCellConfigDialogButton
              handleOpenBasicCellDialog={() => handleOpenBasicCellDialog(rowData, columnIndex)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const updateCellConfig = (cfg: ICellConfig) => {
    if (selectedCellLocation) {
      const newConfig = {
        ...currentConfig,
        rows: currentConfig.rows.map((row, index) => {
          if (index === selectedCellLocation.rowIndex) {
            return {
              ...row,
              columns: row.columns.map((column, columnIndex) => {
                if (columnIndex === selectedCellLocation.columnIndex) {
                  return {
                    ...column,
                    cell: cfg,
                  };
                }
                return column;
              }),
            };
          }
          return row;
        }),
      };
      setCurrentConfig(newConfig);
      setIsEditDatasetCellDialogVisible(false);
    }
  };

  return (
    <div>
      <ToggleButton
        offLabel={T("label.display-full-dataset-titles")}
        onLabel={T("label.truncate-dataset-titles")}
        checked={isDatasetValuesVisible}
        onChange={e => setIsDatasetValuesVisible(e.value)}
        className="mb-2"
      />
      <DataTable
        value={
          currentConfig.rows.length > 0
            ? currentConfig.rows.map((row, index) => ({ ...row, rowIndex: index }))
            : []
        }
        editMode="cell"
        scrollHeight="flex"
        size="small"
      >
        {currentConfig.rows.length > 0 &&
          currentConfig.rows[0]?.columns?.length > 0 &&
          currentConfig.rows[0].columns.map((col, colIdx) => {
            const columnCount = currentConfig.rows[0].columns.length;
            const columnWidth = `${100 / columnCount}%`;

            return (
              <Column
                key={uuidv4()}
                field={`columns[${colIdx}].cell.value`}
                header={currentConfig.columnsOptions[colIdx].title ?? T("word.new")}
                body={rowData => renderCell(rowData, colIdx)}
                style={{ width: columnWidth, minWidth: columnWidth }}
              />
            );
          })}
      </DataTable>

      {isEditDatasetCellDialogVisible && selectedCell && (
        <EditDatasetCellDialog
          isEditDatasetCellDialogVisible={isEditDatasetCellDialogVisible}
          setIsEditDatasetCellDialogVisible={setIsEditDatasetCellDialogVisible}
          selectedDatasetCell={selectedCell}
          updateSelectedDatasetCell={updateCellConfig}
        />
      )}
      {isEditBasicCellDialogVisible && selectedCell && (
        <EditBasicCellDialog
          isEditBasicCellDialogVisible={isEditBasicCellDialogVisible}
          setIsEditBasicCellDialogVisible={setIsEditBasicCellDialogVisible}
          selectedCell={selectedCell}
          updateSelectedCell={updateCellConfig}
        />
      )}
    </div>
  );
};

export default CellValuesEditor;

const OpenCellConfigDialogButton: React.FC<{ handleOpenBasicCellDialog: () => void }> = ({
  handleOpenBasicCellDialog,
}) => {
  return (
    <Button
      icon="pi pi-pencil"
      type="button"
      className="p-button-text"
      onClick={handleOpenBasicCellDialog}
    />
  );
};
